import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, User } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import servicesData from '../data/services.json';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface ChatbotKnowledge {
  categories: string[];
  services: Array<{
    name: string;
    category: string;
    price: number;
    description: string;
    duration?: string;
  }>;
  faqs: Array<{
    question: string;
    answer: string;
  }>;
}

export default function Chatbot() {
  const location = useLocation();
  
  // Don't show chatbot on admin pages
  if (location.pathname.startsWith('/admin')) {
    return null;
  }
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showGreeting, setShowGreeting] = useState(true);
  const [greetingMessage, setGreetingMessage] = useState('Hello, I am your Assistant! 👋');
  const [userActivityDetected, setUserActivityDetected] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hello! 👋 I am your RapsApp Agent.\n\nHow can I help you today?\n\n• Browse our services\n• Check pricing\n• Book a service\n• Service areas\n• Payment options\n\nWhat would you like to know?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Prepare knowledge base from services data
  const knowledgeBase: ChatbotKnowledge = {
    categories: servicesData.categories.map(cat => cat.name),
    services: servicesData.services.map(service => ({
      name: service.name,
      category: servicesData.categories.find(cat => cat.id === service.categoryId)?.name || '',
      price: service.price,
      description: service.description,
      duration: service.duration,
    })),
    faqs: [
      {
        question: 'how to book',
        answer: 'Booking is easy! Just browse our services, select the one you need, choose a date and time, and confirm. You can also call us at +91 7018401540.',
      },
      {
        question: 'payment',
        answer: 'We accept multiple payment methods including UPI, Credit/Debit Cards, Net Banking, and Cash on Service. Payment is processed securely through PhonePe.',
      },
      {
        question: 'service area',
        answer: 'We provide services across Himachal Pradesh including Mandi, Bilaspur, Hamirpur, Kangra, Kullu, Shimla, Solan, Una, and nearby areas.',
      },
      {
        question: 'cancellation',
        answer: 'You can cancel or reschedule your booking up to 2 hours before the scheduled time. Full refund will be processed within 5-7 business days.',
      },
      {
        question: 'professionals verified',
        answer: 'Yes! All our professionals are background-verified, trained, and experienced. We ensure quality and safety for all our services.',
      },
      {
        question: 'timing',
        answer: 'Our services are available 7 days a week from 8:00 AM to 8:00 PM. Emergency services may be available on request.',
      },
      {
        question: 'warranty guarantee',
        answer: 'We provide a 30-day service guarantee on most services. If you\'re not satisfied, we\'ll make it right at no additional cost.',
      },
    ],
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Detect current page
  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '/home') {
      setCurrentPage('home');
    } else if (path.includes('/services')) {
      setCurrentPage('services');
    } else if (path.includes('/service/')) {
      setCurrentPage('serviceDetail');
    } else if (path.includes('/booking')) {
      setCurrentPage('booking');
    } else if (path.includes('/cart')) {
      setCurrentPage('cart');
    } else if (path.includes('/profile')) {
      setCurrentPage('profile');
    } else if (path.includes('/about')) {
      setCurrentPage('about');
    } else if (path.includes('/contact')) {
      setCurrentPage('contact');
    }
  }, [location]);

  // Get page-specific greeting messages
  const getPageGreetings = () => {
    switch(currentPage) {
      case 'home':
        return [
          'Hello, I am your Assistant! 👋',
          'Need help with services? 🛠️',
          'Looking for home services? 🏠',
          'Want to book a service? 📅',
          'Explore our services! ✨',
        ];
      case 'services':
        return [
          'Found what you need? 🔍',
          'Need help choosing? 💡',
          'Want service details? 📋',
          'Ready to book? 📅',
        ];
      case 'serviceDetail':
        return [
          'Interested in this service? 🎯',
          'Want to book this? �',
          'Need more details? 💬',
          'Ready to add to cart? 🛒',
        ];
      case 'booking':
        return [
          'Need help with booking? 📝',
          'Having trouble? I can help! 💁',
          'Questions about the form? ❓',
        ];
      case 'cart':
        return [
          'Ready to checkout? 🛒',
          'Need to add more services? ➕',
          'Questions about payment? 💳',
        ];
      case 'profile':
        return [
          'Complete your profile? 👤',
          'Need profile help? 📝',
          'Update your details? ✏️',
        ];
      default:
        return [
          'Hello, I am your Assistant! 👋',
          'How can I help you? 💬',
        ];
    }
  };

  // Dynamic greeting messages based on user behavior
  useEffect(() => {
    const greetingMessages = getPageGreetings();

    let messageIndex = 0;
    let greetingTimer: ReturnType<typeof setTimeout>;
    let activityTimer: ReturnType<typeof setTimeout>;

    // Change greeting message every 5 seconds
    const rotateGreeting = () => {
      greetingTimer = setTimeout(() => {
        if (!isOpen && showGreeting) {
          messageIndex = (messageIndex + 1) % greetingMessages.length;
          setGreetingMessage(greetingMessages[messageIndex]);
          rotateGreeting();
        }
      }, 5000);
    };

    // Track user activity
    const handleScroll = () => {
      setUserActivityDetected(true);
      if (!isOpen && !showGreeting) {
        setShowGreeting(true);
        setGreetingMessage('Still browsing? I can help! 💬');
      }
      
      // Reset activity after 3 seconds
      clearTimeout(activityTimer);
      activityTimer = setTimeout(() => {
        setUserActivityDetected(false);
      }, 3000);
    };

    const handleMouseMove = () => {
      if (!userActivityDetected) {
        setUserActivityDetected(true);
      }
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Detect if user clicked on service-related elements
      if (target.closest('a[href*="services"]') || 
          target.closest('button') || 
          target.closest('[class*="service"]')) {
        if (!isOpen && !showGreeting) {
          setShowGreeting(true);
          setGreetingMessage('Interested in this service? Ask me! 🎯');
        }
      }
    };

    // Start greeting rotation
    if (!isOpen && showGreeting) {
      rotateGreeting();
    }

    // Add event listeners
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);

    return () => {
      clearTimeout(greetingTimer);
      clearTimeout(activityTimer);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
    };
  }, [isOpen, showGreeting, userActivityDetected, currentPage]);

  // Hide greeting after chat is opened
  const handleOpenChat = () => {
    setIsOpen(true);
    
    // Send automatic message based on the greeting context
    const contextMessage = getContextMessage(greetingMessage);
    if (contextMessage) {
      // Add user's contextual message
      const userMessage: Message = {
        id: Date.now().toString(),
        text: contextMessage,
        sender: 'user',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, userMessage]);
      
      // Generate bot response after a short delay
      setTimeout(() => {
        setIsTyping(true);
        setTimeout(() => {
          const botMessage: Message = {
            id: (Date.now() + 1).toString(),
            text: generateResponse(contextMessage),
            sender: 'bot',
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, botMessage]);
          setIsTyping(false);
        }, 1000);
      }, 500);
    }
    
    setShowGreeting(false);
    
    // Re-show greeting after 30 seconds if chat is closed and user is still active
    setTimeout(() => {
      if (!isOpen && userActivityDetected) {
        setShowGreeting(true);
        setGreetingMessage('Need more help? I\'m here! 😊');
      }
    }, 30000);
  };

  // Get contextual message based on greeting
  const getContextMessage = (greeting: string): string => {
    if (greeting.includes('Need help with services')) {
      return 'What services do you offer?';
    } else if (greeting.includes('Looking for home services')) {
      return 'Show me home services';
    } else if (greeting.includes('Want to book a service')) {
      return 'How can I book a service?';
    } else if (greeting.includes('I can help you find services')) {
      return 'Help me find a service';
    } else if (greeting.includes('Explore our services')) {
      return 'Show me all services';
    } else if (greeting.includes('Still browsing')) {
      return 'Yes, I need help with something';
    } else if (greeting.includes('Interested in this service')) {
      return 'Tell me more about this service';
    } else if (greeting.includes('Need more help')) {
      return 'Yes, I have a question';
    } else if (greeting.includes('Hello, I am your Assistant')) {
      return ''; // Default greeting - no auto message
    }
    return '';
  };

  // Intelligent response generation
  const generateResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();

    // Page-specific responses
    // Profile completion guidance
    if (lowerMessage.includes('profile') || lowerMessage.includes('complete profile') || lowerMessage.includes('cant book') || lowerMessage.includes('can\'t book') || lowerMessage.includes('unable to book')) {
      return '👤 **Complete Your Profile for Smooth Booking!**\n\n' +
        'To book services, you need to complete your profile first. This helps us:\n\n' +
        '✅ Provide personalized service\n' +
        '✅ Contact you for updates\n' +
        '✅ Process bookings faster\n' +
        '⏱️ Save time (less than 1 minute!)\n\n' +
        '**Required Information:**\n' +
        '• Full Name\n' +
        '• Phone Number\n' +
        '• Email Address\n' +
        '• Service Address\n' +
        '• City/Area\n\n' +
        '**Benefits:**\n' +
        '🚀 Future bookings in under 1 minute\n' +
        '📱 Get instant updates\n' +
        '💰 Access exclusive offers\n' +
        '📋 Track your service history\n\n' +
        '**Ready to complete your profile?**\n' +
        'Click "Go to Profile" or let me guide you!\n\n' +
        'Would you like me to:\n' +
        '• Help you fill the profile?\n' +
        '• Explain why each field is needed?\n' +
        '• Show you how to update it later?';
    }

    // Cart page help
    if ((currentPage === 'cart' || lowerMessage.includes('cart') || lowerMessage.includes('checkout')) && !lowerMessage.includes('add to cart')) {
      return '🛒 **Ready to Complete Your Booking?**\n\n' +
        '**Before checkout, make sure:**\n' +
        '✅ Your profile is complete (Name, Phone, Address)\n' +
        '✅ You\'ve selected the right services\n' +
        '✅ Date and time are correct\n\n' +
        '**What happens next:**\n' +
        '1️⃣ Review your cart\n' +
        '2️⃣ Complete profile (if not done)\n' +
        '3️⃣ Choose payment method\n' +
        '4️⃣ Confirm booking\n' +
        '5️⃣ Get instant confirmation!\n\n' +
        '**Payment Options:**\n' +
        '💳 Credit/Debit Cards\n' +
        '📱 UPI (PhonePe, GPay, Paytm)\n' +
        '🏦 Net Banking\n' +
        '💵 Cash on Service\n\n' +
        '⚠️ **Important:** If your profile is incomplete, you\'ll be redirected to complete it first.\n\n' +
        'Need help with:\n' +
        '• Completing your profile?\n' +
        '• Adding more services?\n' +
        '• Payment methods?\n' +
        '• Booking process?';
    }

    // Service detail page help
    if (currentPage === 'serviceDetail' || lowerMessage.includes('add to cart') || lowerMessage.includes('this service')) {
      return '🎯 **Interested in This Service?**\n\n' +
        '**How to Book:**\n' +
        '1️⃣ Click "Add to Cart"\n' +
        '2️⃣ Select date and time\n' +
        '3️⃣ Review service details\n' +
        '4️⃣ Proceed to checkout\n\n' +
        '⚠️ **First-time user?**\n' +
        'You\'ll need to complete your profile before booking. It takes less than 1 minute!\n\n' +
        '**What you\'ll need:**\n' +
        '• Your name and phone\n' +
        '• Service address\n' +
        '• Preferred date/time\n\n' +
        '**This service includes:**\n' +
        '✅ Professional service provider\n' +
        '✅ Quality guarantee\n' +
        '✅ On-time arrival\n' +
        '✅ 30-day service warranty\n\n' +
        'Would you like to:\n' +
        '• See similar services?\n' +
        '• Check pricing details?\n' +
        '• Add to cart now?\n' +
        '• Complete your profile first?';
    }

    // Booking page help
    if (currentPage === 'booking' || lowerMessage.includes('booking') || lowerMessage.includes('how to book')) {
      const profileComplete = localStorage.getItem('userProfile');
      if (!profileComplete) {
        return '📝 **Let\'s Complete Your Booking!**\n\n' +
          '⚠️ **Profile Required:** I noticed your profile isn\'t complete yet.\n\n' +
          '**Why complete your profile?**\n' +
          '✅ Enable instant bookings\n' +
          '✅ Get service updates via SMS/Email\n' +
          '✅ Save time on future bookings (under 1 min!)\n' +
          '✅ Track your service history\n' +
          '✅ Access exclusive offers\n\n' +
          '**Quick Profile Setup (Less than 1 minute):**\n' +
          '1️⃣ Full Name - So we know who to greet! 👋\n' +
          '2️⃣ Phone Number - For booking confirmations 📱\n' +
          '3️⃣ Email - For receipts and updates 📧\n' +
          '4️⃣ Address - Where to provide service 📍\n' +
          '5️⃣ City/Area - To assign local professionals 🏠\n\n' +
          '**Ready to complete your profile?**\n' +
          'I can guide you through each step!\n\n' +
          'Or call us: +91 7018401540';
      }
      return '📅 **Booking is Easy!**\n\n' +
        '✅ Your profile is complete - Great!\n\n' +
        '**Steps to Book:**\n' +
        '1️⃣ Choose your service\n' +
        '2️⃣ Select date & time\n' +
        '3️⃣ Confirm service address\n' +
        '4️⃣ Choose payment method\n' +
        '5️⃣ Complete booking\n\n' +
        '**Tips for smooth booking:**\n' +
        '• Book at least 2 hours in advance\n' +
        '• Double-check your address\n' +
        '• Be available at service time\n\n' +
        'Need help with:\n' +
        '• Selecting date/time?\n' +
        '• Payment options?\n' +
        '• Service details?';
    }

    // Thanks/Goodbye
    if (lowerMessage.includes('thank') || lowerMessage.includes('thanks')) {
      return 'You\'re very welcome! 😊\n\nIs there anything else I can help you with?\n\n• View other services\n• Check pricing\n• Contact information\n• Book a service';
    }

    if (lowerMessage.includes('bye') || lowerMessage.includes('goodbye') || lowerMessage.includes('see you')) {
      return 'Thank you for choosing RAPSAPP! 👋\n\nWe look forward to serving you. Have a great day!\n\n📞 Need help anytime? Call us at +91 7018401540';
    }

    // Greetings
    if (/^(hi|hello|hey|good morning|good evening|namaste)/.test(lowerMessage)) {
      let greeting = 'Hello! 😊 I am your RAPSAPP Agent.\n\n';
      
      // Add page-specific greeting
      if (currentPage === 'services') {
        greeting += 'I see you\'re browsing our services! 🔍\n\n';
      } else if (currentPage === 'serviceDetail') {
        greeting += 'Interested in this service? Let me help! 🎯\n\n';
      } else if (currentPage === 'cart') {
        greeting += 'Ready to checkout? I can help! 🛒\n\n';
      } else if (currentPage === 'booking') {
        greeting += 'Need help with booking? I\'m here! 📝\n\n';
      }
      
      greeting += 'I can help you with:\n\n• Service information\n• Booking details\n• Pricing\n• Profile completion\n• Service areas\n• Payment methods\n\nWhat would you like to explore?';
      
      return greeting;
    }

    // Service categories
    if (lowerMessage.includes('service') && (lowerMessage.includes('what') || lowerMessage.includes('which') || lowerMessage.includes('list') || lowerMessage.includes('show'))) {
      return `We offer the following services:\n\n${knowledgeBase.categories.map((cat, i) => `${i + 1}. ${cat}`).join('\n')}\n\nWhich service interests you?\n\nOr would you like to:\n• See pricing for all services\n• Know about booking process\n• Check service areas`;
    }

    // Beauty services
    if (lowerMessage.includes('beauty') || lowerMessage.includes('facial') || lowerMessage.includes('makeup') || lowerMessage.includes('hair')) {
      const beautyServices = knowledgeBase.services.filter(s => s.category === 'Beauty Services').slice(0, 5);
      return `Our Beauty Services include:\n\n${beautyServices.map(s => `• ${s.name} - ₹${s.price} (${s.duration})\n  ${s.description}`).join('\n\n')}\n\nWould you like to:\n• Book any of these services?\n• See more beauty services?\n• Check other service categories?\n• Know payment methods?`;
    }

    // Plumbing services
    if (lowerMessage.includes('plumb') || lowerMessage.includes('leak') || lowerMessage.includes('pipe') || lowerMessage.includes('tap')) {
      const plumbingServices = knowledgeBase.services.filter(s => s.category === 'Plumbing').slice(0, 5);
      return `Our Plumbing Services include:\n\n${plumbingServices.map(s => `• ${s.name} - ₹${s.price}\n  ${s.description}`).join('\n\n')}\n\nNeed immediate plumbing help? Call us at +91 7018401540\n\nWould you like to:\n• Book a plumber?\n• See other services?\n• Know about emergency service?\n• Check pricing details?`;
    }

    // Electrical services
    if (lowerMessage.includes('electric') || lowerMessage.includes('wiring') || lowerMessage.includes('switch') || lowerMessage.includes('light')) {
      const electricalServices = knowledgeBase.services.filter(s => s.category === 'Electrical').slice(0, 5);
      return `Our Electrical Services include:\n\n${electricalServices.map(s => `• ${s.name} - ₹${s.price}\n  ${s.description}`).join('\n\n')}\n\nSafety first! Our electricians are certified and experienced.\n\nWould you like to:\n• Book an electrician?\n• See other services?\n• Know about warranty?\n• Check service areas?`;
    }

    // Cleaning services
    if (lowerMessage.includes('clean') || lowerMessage.includes('maid') || lowerMessage.includes('house') || lowerMessage.includes('deep clean')) {
      const cleaningServices = knowledgeBase.services.filter(s => s.category === 'Cleaning').slice(0, 5);
      return `Our Cleaning Services include:\n\n${cleaningServices.map(s => `• ${s.name} - ₹${s.price}\n  ${s.description}`).join('\n\n')}\n\nProfessional cleaning with eco-friendly products!\n\nWould you like to:\n• Book cleaning service?\n• See package deals?\n• Know about deep cleaning?\n• Check other services?`;
    }

    // Carpentry services
    if (lowerMessage.includes('carpen') || lowerMessage.includes('furniture') || lowerMessage.includes('wood') || lowerMessage.includes('door')) {
      const carpentryServices = knowledgeBase.services.filter(s => s.category === 'Carpentry').slice(0, 5);
      return `Our Carpentry Services include:\n\n${carpentryServices.map(s => `• ${s.name} - ₹${s.price}\n  ${s.description}`).join('\n\n')}\n\nCustom solutions for all your woodwork needs!\n\nWould you like to:\n• Book a carpenter?\n• Get custom furniture quote?\n• See other services?\n• Know about materials used?`;
    }

    // Painting services
    if (lowerMessage.includes('paint') || lowerMessage.includes('wall') || lowerMessage.includes('color')) {
      const paintingServices = knowledgeBase.services.filter(s => s.category === 'Painting').slice(0, 5);
      return `Our Painting Services include:\n\n${paintingServices.map(s => `• ${s.name} - ₹${s.price}\n  ${s.description}`).join('\n\n')}\n\nTransform your space with professional painting!\n\nWould you like to:\n• Book painting service?\n• Get color consultation?\n• See other services?\n• Know about paint quality?`;
    }

    // Pricing questions
    if (lowerMessage.includes('price') || lowerMessage.includes('cost') || lowerMessage.includes('charge') || lowerMessage.includes('rate')) {
      return 'Our prices are competitive and transparent! 💰\n\nPrices vary by service:\n• Beauty Services: ₹299 - ₹2,999\n• Plumbing: ₹199 - ₹4,999\n• Electrical: ₹149 - ₹3,999\n• Cleaning: ₹399 - ₹2,499\n• Carpentry: ₹299 - ₹9,999\n• Painting: ₹999 - ₹14,999\n\nWhich service pricing would you like to know in detail?\n\nOr would you like to:\n• See all services\n• Book a service\n• Know about discounts';
    }

    // Check FAQs
    for (const faq of knowledgeBase.faqs) {
      if (lowerMessage.includes(faq.question)) {
        return `${faq.answer}\n\nIs there anything else you\'d like to know?\n\n• Book a service\n• Check other services\n• Know about payment\n• View service areas`;
      }
    }

    // Contact information
    if (lowerMessage.includes('contact') || lowerMessage.includes('phone') || lowerMessage.includes('call') || lowerMessage.includes('whatsapp')) {
      return '📞 Contact Us:\n\n• Phone: +91 7018401540\n• WhatsApp: +91 7018401540\n• Email: support@rapsapp.com\n\nYou can also chat with us on WhatsApp for instant support!\n\nWould you like to:\n• Book a service now?\n• Know service hours?\n• Check service areas?\n• See our services?';
    }

    // Location/Service area
    if (lowerMessage.includes('location') || lowerMessage.includes('area') || lowerMessage.includes('where') || lowerMessage.includes('available')) {
      return '📍 We serve across Himachal Pradesh:\n\n• Mandi\n• Bilaspur\n• Hamirpur\n• Kangra\n• Kullu\n• Shimla\n• Solan\n• Una\n• And nearby areas\n\nIs your area covered?\n\nWould you like to:\n• Book a service?\n• See available services?\n• Know about pricing?\n• Check booking process?';
    }

    // Booking questions
    if (lowerMessage.includes('book') || lowerMessage.includes('schedule') || lowerMessage.includes('appointment')) {
      return '📅 Booking is simple!\n\n1. Browse our services\n2. Select the service you need\n3. Choose date & time\n4. Enter your details\n5. Confirm booking\n\nOr call us at +91 7018401540 for assistance!\n\nWould you like to:\n• See all services?\n• Know about pricing?\n• Check payment methods?\n• Contact us directly?';
    }

    // Detailed service information (for "Tell me more about this service")
    if (lowerMessage.includes('tell me more') || lowerMessage.includes('service detail') || lowerMessage.includes('more about') || lowerMessage.includes('this service')) {
      return '🏠 **Welcome to RAPSAPP - Your Complete Home Services Solution!**\n\n' +
        '✨ **What We Offer:**\n\n' +
        '1️⃣ **Beauty Services** 💄\n' +
        '   • Bridal Makeup, Party Makeup, Hair Styling\n' +
        '   • Facials, Bleaching, Waxing, Pedicure/Manicure\n' +
        '   • Price Range: ₹299 - ₹2,999\n\n' +
        
        '2️⃣ **Plumbing Services** 🔧\n' +
        '   • Tap/Pipe Repair, Leak Fixing, Drainage Cleaning\n' +
        '   • Bathroom/Kitchen Installation\n' +
        '   • Price Range: ₹199 - ₹4,999\n' +
        '   • Emergency services available!\n\n' +
        
        '3️⃣ **Electrical Services** ⚡\n' +
        '   • Wiring, Switch/Socket Installation\n' +
        '   • Fan/Light Installation, MCB Repair\n' +
        '   • Price Range: ₹149 - ₹3,999\n' +
        '   • Certified electricians only!\n\n' +
        
        '4️⃣ **Cleaning Services** 🧹\n' +
        '   • Deep Cleaning, Regular Cleaning\n' +
        '   • Kitchen/Bathroom Cleaning\n' +
        '   • Price Range: ₹399 - ₹2,499\n' +
        '   • Eco-friendly products used!\n\n' +
        
        '5️⃣ **Carpentry Services** 🪚\n' +
        '   • Furniture Repair, Door/Window Fixing\n' +
        '   • Custom Furniture, Modular Solutions\n' +
        '   • Price Range: ₹299 - ₹9,999\n\n' +
        
        '6️⃣ **Painting Services** 🎨\n' +
        '   • Interior/Exterior Painting, Wall Texturing\n' +
        '   • Waterproofing, Wood Polishing\n' +
        '   • Price Range: ₹999 - ₹14,999\n\n' +
        
        '📍 **Service Areas:** All across Himachal Pradesh\n' +
        '⏰ **Availability:** 7 Days a Week, 8 AM - 8 PM\n' +
        '✅ **Quality Promise:** 30-day service guarantee\n' +
        '🔒 **Safety:** Background-verified professionals\n' +
        '💳 **Payment:** UPI, Cards, Cash - All accepted!\n\n' +
        
        '**Ready to book?**\n\n' +
        '• Select a specific service category\n' +
        '• Get detailed pricing\n' +
        '• Schedule your service\n' +
        '• Or call us: +91 7018401540\n\n' +
        
        'Which service category interests you most?';
    }

    // Default response
    return 'I\'m here to help! I can assist you with:\n\n• Service information and pricing\n• Booking and scheduling\n• Service areas\n• Payment methods\n• Cancellation policy\n\nWhat would you like to know?\n\nYou can also:\n📞 Call: +91 7018401540\n💬 WhatsApp: +91 7018401540';
  };

  const handleSendMessage = () => {
    if (!inputText.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);

    // Simulate bot thinking and respond
    setTimeout(() => {
      const botResponse = generateResponse(inputText);
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: botResponse,
        sender: 'bot',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 800);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Quick action buttons - updated based on current page
  const getQuickActions = () => {
    switch(currentPage) {
      case 'serviceDetail':
        return [
          'Add to cart',
          'Service details',
          'Check pricing',
          'Complete profile',
        ];
      case 'cart':
        return [
          'Complete profile',
          'Payment options',
          'Add more services',
          'How to checkout?',
        ];
      case 'booking':
        return [
          'Complete my profile',
          'Help with booking form',
          'Payment methods',
          'Contact support',
        ];
      case 'profile':
        return [
          'Why complete profile?',
          'What info needed?',
          'Privacy & security',
          'Browse services',
        ];
      default:
        return [
          'What services do you offer?',
          'How to book a service?',
          'Show me pricing',
          'Contact information',
        ];
    }
  };

  const quickActions = getQuickActions();

  const handleQuickAction = (action: string) => {
    // Handle special navigation actions
    if (action === 'Add to cart' || action === 'Complete profile' || action === 'Complete my profile') {
      if (action.includes('profile')) {
        // Check if already on profile page
        if (currentPage !== 'profile') {
          setMessages(prev => [...prev, {
            id: Date.now().toString(),
            text: '👤 Redirecting you to complete your profile...',
            sender: 'bot',
            timestamp: new Date(),
          }]);
          setTimeout(() => navigate('/profile'), 1000);
          return;
        }
      }
    }
    
    setInputText(action);
    setTimeout(() => handleSendMessage(), 100);
  };

  return (
    <>
      {/* Chatbot Toggle Button */}
      <AnimatePresence>
        {!isOpen && showGreeting && (
          <>
            {/* Greeting Text with Jump Animation */}
            <motion.div
              initial={{ opacity: 0, x: 50, y: 0 }}
              animate={{ 
                opacity: 1, 
                x: 0,
                y: [0, -10, 0, -5, 0], // Jump effect
              }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ 
                opacity: { delay: 0.5, duration: 0.3 },
                x: { delay: 0.5, duration: 0.3 },
                y: { 
                  delay: 0.8, 
                  duration: 0.6,
                  repeat: Infinity,
                  repeatDelay: 3,
                  ease: "easeInOut"
                }
              }}
              className="fixed bottom-24 md:bottom-6 right-20 md:right-24 z-[60] bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-2 md:px-5 md:py-3 rounded-2xl shadow-lg"
            >
              {/* Thinking dots animation */}
              <div className="flex items-center gap-2">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 2.4 }}
                  className="w-1.5 h-1.5 bg-white rounded-full"
                />
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 2.4, delay: 0.2 }}
                  className="w-1.5 h-1.5 bg-white rounded-full"
                />
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 2.4, delay: 0.4 }}
                  className="w-1.5 h-1.5 bg-white rounded-full"
                />
              </div>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="text-xs md:text-sm font-medium text-white whitespace-nowrap mt-1"
              >
                {greetingMessage}
              </motion.p>
              
              {/* Speech bubble arrow */}
              <div className="absolute right-[-8px] top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-l-[8px] border-l-primary-600"></div>
            </motion.div>

            {/* Floating Button */}
            <motion.button
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleOpenChat}
              className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-[60] bg-white p-2 md:p-2.5 rounded-full shadow-2xl hover:shadow-primary-500/50 transition-all duration-300"
            >
              <img src="/favicon.ico" alt="Chat" className="h-8 w-8 md:h-10 md:w-10" />
              {/* Notification dot */}
              <span className="absolute -top-1 -right-1 h-3 w-3 md:h-4 md:w-4 bg-red-500 rounded-full animate-pulse"></span>
            </motion.button>
          </>
        )}
      </AnimatePresence>

      {/* Chatbot Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-24 md:bottom-6 right-2 md:right-6 z-[60] w-[calc(100vw-1rem)] md:w-96 h-[500px] md:h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white p-3 md:p-4 flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-white rounded-full flex items-center justify-center p-1.5 md:p-2">
                  <img src="/favicon.ico" alt="RAPSAPP" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h3 className="font-bold text-sm md:text-lg">RAPSAPP Assistant</h3>
                  <p className="text-xs text-white/80 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-400 rounded-full animate-pulse"></span>
                    Online
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/20 p-1.5 md:p-2 rounded-full transition-colors"
              >
                <X className="h-4 w-4 md:h-5 md:w-5" />
              </button>
            </div>

            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 md:space-y-4 bg-gray-50">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex gap-1.5 md:gap-2 max-w-[85%] md:max-w-[80%] ${message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                    {/* Avatar */}
                    <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.sender === 'bot' 
                        ? 'bg-white p-1 md:p-1.5'
                        : 'bg-gray-300 text-gray-700'
                    }`}>
                      {message.sender === 'bot' ? (
                        <img src="/favicon.ico" alt="RAPSAPP" className="w-full h-full object-contain" />
                      ) : (
                        <User className="h-3 w-3 md:h-4 md:w-4" />
                      )}
                    </div>

                    {/* Message Bubble */}
                    <div className={`rounded-2xl md:rounded-2xl px-3 py-2 md:px-4 md:py-2 ${
                      message.sender === 'bot'
                        ? 'bg-white text-gray-800 shadow-sm border border-gray-200'
                        : 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white'
                    }`}>
                      <p className="text-xs md:text-sm whitespace-pre-line leading-relaxed">{message.text}</p>
                      <p className={`text-[10px] md:text-xs mt-1 ${message.sender === 'bot' ? 'text-gray-500' : 'text-white/70'}`}>
                        {message.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-1.5 md:gap-2"
                >
                  <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-white flex items-center justify-center p-1 md:p-1.5">
                    <img src="/favicon.ico" alt="RAPSAPP" className="w-full h-full object-contain" />
                  </div>
                  <div className="bg-white rounded-2xl px-3 py-2 md:px-4 md:py-3 shadow-sm border border-gray-200">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gray-400 rounded-full animate-bounce"></span>
                      <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                      <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {messages.length === 1 && (
              <div className="px-3 md:px-4 py-2 bg-white border-t border-gray-200">
                <p className="text-[10px] md:text-xs text-gray-600 mb-2">Quick actions:</p>
                <div className="flex flex-wrap gap-1.5 md:gap-2">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickAction(action)}
                      className="text-[10px] md:text-xs bg-gray-100 hover:bg-primary-50 text-gray-700 hover:text-primary-700 px-2 py-1 md:px-3 md:py-1.5 rounded-full transition-colors border border-gray-200 hover:border-primary-300"
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="p-3 md:p-4 bg-white border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="flex-1 px-3 py-2 md:px-4 md:py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-xs md:text-sm"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputText.trim()}
                  className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white p-2 rounded-full hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <Send className="h-4 w-4 md:h-5 md:w-5" />
                </button>
              </div>
              <p className="text-[10px] md:text-xs text-gray-500 mt-2 text-center">
                Powered by RapsApp AI Assistant
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
